import React, { FormEventHandler, MouseEventHandler, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectInput from "../common/SelectInput";
import SchemaForm from "../common/SchemaForm";
import { NewIntegration, DestinationType, Organization, Member } from "../common/ObjectTypes";
import { ArrowDown, ArrowRight, AttentionIcon } from "../common/ProjectIcons";
import { checkSchema } from "../common/SchemaFormTemplates/checkSchema";
import { isHiddenUrlDestinationType } from "./isHiddenUrlDestinationType";
import { getButtonText } from "../common/TestActionFunctions";
import { Tooltip } from "../common/Tooltip";
import { useScrollLock } from "../common/useScrollLock";
import ReactGA4 from "react-ga4";
import { getEnvironment } from "../common/getEnvironment";

type DestinationModalProps = {
    currentMemberIsAdmin: boolean,
    errors: any,
    rsjfErrors: any,
    onToggleModal: MouseEventHandler,
    onNextStep: MouseEventHandler,
    stepOne: boolean,
    stepTwo: boolean,
    destination: NewIntegration,
    destinationType: DestinationType,
    organizations: Organization[],
    loadMoreOrgs: Function,
    orgInputChange: Function
    destinationTypes: DestinationType[],
    onChange: Function,
    onChangeOrg: Function,
    onChangeType: Function,
    onChangeJSONForm: Function,
    onSave: FormEventHandler
}

const DestinationModal = ({
    currentMemberIsAdmin,
    errors,
    rsjfErrors,
    onToggleModal,
    onNextStep,
    stepOne,
    stepTwo,
    destination,
    destinationType,
    organizations,
    loadMoreOrgs,
    orgInputChange,
    destinationTypes,
    onChange,
    onChangeOrg,
    onChangeType,
    onChangeJSONForm,
    onSave
}: DestinationModalProps) => {
    const { t } = useTranslation();
    const [showAdvanced, setShowAdvanced] = useState(false);
    const shouldHideUrlField = isHiddenUrlDestinationType(destinationType.name);

    useScrollLock(true);
    const formInteractionStarted = useRef(false);

    const handleFormInteraction = () => {
        if (!formInteractionStarted.current) {
            const currentEnv = getEnvironment();
            ReactGA4.event('form_start', {
                form_name: 'new_destination_form',
                environment: currentEnv
            });
            formInteractionStarted.current = true;
        }
    };
    return (
        <div className="fixed inset-0 z-50">
            <div className="fixed inset-0 bg-dark-gray dark:bg-neutral-gray-900 opacity-80 dark:opacity-90 transition-colors duration-300"
                onClick={onToggleModal}></div>
            <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
                <div className="z-20 flex flex-col bg-very-light-gray dark:bg-dark-gray w-full sm:w-5/6 md:w-3/4 lg:w-2/3 xl:w-1/2 
                               h-[75vh] my-4 shadow rounded-lg overflow-y-auto dark:text-white transition-colors duration-300">
                    <div className="flex flex-row-reverse sticky top-0 bg-very-light-gray dark:bg-dark-gray z-10 transition-colors duration-300">
                        <button
                            type="button"
                            onClick={onToggleModal}
                            className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white"
                            data-modal-hide="authentication-modal"
                        >
                            <svg aria-hidden="true" className="w-7 h-7 text-off-black dark:text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                            <span className="sr-only">{t("Close Modal")}</span>
                        </button>
                    </div>
                    <div className="pb-6 h-full">
                        <div onSubmit={onSave} className="h-full">
                            <div className="flex flex-col h-full">
                                {/* STEP 1: DESTINATION TYPE */}
                                {stepOne &&
                                    <div className="px-4 sm:px-6 h-full flex flex-col justify-between">
                                        <div>
                                            <h1 className="text-2xl sm:text-3xl font-semibold mb-4 sm:mb-8 dark:text-white transition-colors duration-300">{t("New Destination")}</h1>
                                            {/* form fields */}
                                            <div className="flex flex-col">
                                                <label>{t("Type")}</label>
                                                <SelectInput
                                                    options={destinationTypes.map((type: DestinationType) => {
                                                        return {
                                                            label: type.name,
                                                            name: type.name,
                                                            description: type.description,
                                                            id: type.id,
                                                            value: type.id,
                                                            actions: type.actions
                                                        }
                                                    })}
                                                    className={"basic-single text-black"}
                                                    defaultValue={{
                                                        label: "Select",
                                                        value: 0
                                                    }}
                                                    isDisabled={false}
                                                    isLoading={false}
                                                    isClearable={false}
                                                    isSearchable={true}
                                                    isMulti={false}
                                                    name={t("Type")}
                                                    onFocus={handleFormInteraction}
                                                    onChange={(e: any) => onChangeType(e)}
                                                />
                                            </div>
                                        </div>
                                        <div className="sticky bottom-0 bg-white/50 dark:bg-dark-gray/50 backdrop-blur-sm flex flex-row mt-2 py-2 transition-colors duration-300">
                                            {/* next button */}
                                            <button
                                                className="bg-dark-green hover:bg-status-green text-white font-bold rounded px-7 py-2"
                                                onClick={onNextStep}>
                                                {t("Next")}
                                            </button>
                                            <button className="bg-very-light-gray dark:bg-neutral-gray-700 text-medium-gray dark:text-gray-lines rounded px-7 mx-2 transition-colors duration-300"
                                                type="button"
                                                onClick={onToggleModal}>
                                                {t("Cancel")}
                                            </button>
                                        </div>
                                    </div>}

                                {/* STEP 2: NAME, CONFIGURATION, ENABLED */}
                                {stepTwo &&
                                    <div className="h-full w-full mb-8 flex flex-col justify-between">
                                        <div className="px-4 sm:px-6 w-full">
                                            <h1 className="text-3xl font-semibold mb-8 dark:text-white transition-colors duration-300">{t("New")} {destinationType.name} {t("Destination")}</h1>
                                            {/* form fields */}
                                            {/* Name */}
                                            <div className="flex flex-col pb-1">
                                                <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("Name")} *</label>
                                                <input
                                                    id="name"
                                                    placeholder=""
                                                    className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-neutral-gray-700 dark:bg-dark-gray rounded rounded-sm px-2 dark:text-white transition-colors duration-300"
                                                    value={destination.name ? destination.name : ""}
                                                    onChange={(e) => onChange(e)}>
                                                </input>
                                                {errors.name && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.name}</p></div>}
                                            </div>
                                            {/* owner: dropdown of all organizations */}
                                            <div className="flex flex-col pb-1">
                                                <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("Workspace")} *</label>
                                                <SelectInput
                                                    options={organizations.map((org: Organization) => {
                                                        return {
                                                            label: org.name,
                                                            value: org.id
                                                        }
                                                    })}
                                                    className={"basic-single text-black"}
                                                    defaultValue={{
                                                        label: "Select",
                                                        value: 0
                                                    }}
                                                    isDisabled={false}
                                                    isLoading={false}
                                                    isClearable={false}
                                                    isSearchable={true}
                                                    isMulti={false}
                                                    name={t("Workspace")}
                                                    onChange={(e: any) => onChangeOrg(e)}
                                                    onMenuScrollToBottom={loadMoreOrgs}
                                                    handleInputChange={orgInputChange}
                                                />
                                                {errors.owner && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.owner}</p></div>}
                                            </div>
                                            {/* URL */}
                                            {!shouldHideUrlField && <div className="flex flex-col">
                                                <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("URL")} *</label>
                                                <input
                                                    id="base_url"
                                                    placeholder=""
                                                    className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-neutral-gray-700 dark:bg-dark-gray rounded rounded-sm px-2 dark:text-white transition-colors duration-300"
                                                    value={destination.base_url ? destination.base_url : ""}
                                                    onChange={(e) => onChange(e)}>
                                                </input>
                                                {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                            </div>}
                                            {/* configuration */}
                                            <div className="pt-14">
                                                {/* <label>Configurations</label> */}
                                                {destinationType.actions.filter((item: any) => item.type !== "pull").map((item: any) => {
                                                    const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                                    const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                                                    return (
                                                        <div key={item.id} className={checkSchema(item) ? "pb-14" : "hidden"}>
                                                            {checkSchema(item) && (
                                                                <>
                                                                    <div className="flex justify-between items-start w-full mb-4">
                                                                        <div className="text-lg font-semibold text-secondary-gray dark:text-gray-300 transition-all duration-300">{formTitle}</div>
                                                                        {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true && (
                                                                            <div className="flex flex-row items-center">
                                                                                <button disabled className="opacity-100 bg-light-gray-button text-secondary-gray font-semibold rounded py-2 px-4 ml-1 mr-3 shadow">
                                                                                    {getButtonText(item.value, t)}
                                                                                </button>
                                                                                <Tooltip description={t("Save your destination first to enable this feature.")} position="right" />
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div className="rounded border border-gray-lines dark:border-neutral-gray-600 bg-white dark:bg-dark-gray p-5 mb-8 transition-colors duration-300">
                                                                        <SchemaForm
                                                                            userIsAdmin={currentMemberIsAdmin}
                                                                            formTitle={''}
                                                                            schema={item.schema}
                                                                            ui_schema={uiSchema}
                                                                            onChange={(e: any) => onChangeJSONForm(e, item.id)}
                                                                            isEditable={true}
                                                                            errors={rsjfErrors[item.id]}
                                                                        />
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                            {/* advanced configurations */}
                                            {shouldHideUrlField && <div className="pb-14">
                                                <div className="mt-6 mb text-lg font-semibold text-secondary-gray dark:text-gray-lines cursor-pointer transition-colors duration-300" onClick={() => setShowAdvanced(!showAdvanced)}>
                                                    {showAdvanced ? <ArrowDown /> : <ArrowRight />} {t("Advanced Configurations")}
                                                </div>
                                                {showAdvanced &&
                                                    <div className="ml-8 flex flex-col">
                                                        <label className="text-sm text-secondary-gray dark:text-gray-lines transition-colors duration-300">{t("URL")}</label>
                                                        <input
                                                            id="base_url"
                                                            placeholder=""
                                                            className="mt-2 p-1.5 border border-neutral-gray-200 dark:border-neutral-gray-700 dark:bg-dark-gray rounded rounded-sm px-2 dark:text-white transition-colors duration-300"
                                                            value={destination.base_url ? destination.base_url : ""}
                                                            onChange={(e) => onChange(e)}>
                                                        </input>
                                                        {errors.base_url && <div className="text-orange-600 py-1 rounded flex flex-row items-center"><AttentionIcon /><p className="ml-1 mt-0.5">{errors.base_url}</p></div>}
                                                    </div>}
                                            </div>}
                                        </div>
                                        {/* submit and cancel button */}
                                        <div className="sticky bottom-0 bg-white/50 dark:bg-dark-gray/50 backdrop-blur-sm flex flex-row my-3 py-3 px-4 sm:px-6 w-full transition-colors duration-300">
                                            <button className="bg-dark-green hover:bg-status-green text-white font-bold rounded px-7 py-2"
                                                type="submit"
                                                onClick={onSave}>
                                                {t("Add")}
                                            </button>
                                            <button className="font-bold text-medium-gray dark:text-gray-lines rounded px-7 mx-2 transition-colors duration-300"
                                                type="button"
                                                onClick={onToggleModal}>
                                                {t("Cancel")}
                                            </button>
                                        </div>
                                    </div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DestinationModal;